/**
 * CharacterMovementComponent - first- and third-person walking controls.
 * Handles velocity-based movement with acceleration, look rotation, jumping,
 * and optional character controller physics for collision.
 */

import * as THREE from 'three';

import { CHARACTER_HEIGHT, CHARACTER_JUMP_SPEED, CHARACTER_MASS, CHARACTER_MAX_SPEED } from '../../const.js';
import { PhysicsMotionType } from '../../physics/PhysicsTypes.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { MathHelpers } from '../../utils/MathHelpers.js';
import { property } from '../../utils/serialization/decorator.js';
import { PrimitiveComponent } from '../PrimitiveComponent.js';

import { BasePawnMovementComponent, MovementMode } from './BasePawnMovementComponent.js';

import type { MovementStateSnapshot, PawnInput } from './BasePawnMovementComponent.js';
import type { CharacterControllerOptions } from '../../physics/PhysicsEngine.js';
import type { SceneComponentOptions } from '../SceneComponent.js';

/**
 * Configuration options for first person movement
 */
export interface CharacterMovementOptions extends SceneComponentOptions {
  /** Movement speed in units per second */
  maxSpeed?: number;
  /** damp lambda for acceleration */
  accelerationLambda?: number;
  /** damp lambda for deceleration */
  decelerationLambda?: number;
  /** Look right/left rotation speed in radians per second */
  lookRightSpeed?: number;
  /** Look up/down rotation speed in radians per second */
  lookUpSpeed?: number;
  /** Jump speed in units per second */
  jumpSpeed?: number;
  /** Maximum number of jumps allowed in mid-air (0 = no mid-air jumps, 1 = double jump, etc.) */
  maxMidAirJumps?: number;
  /** Acceleration lambda for mid-air movement when using the character controller */
  midAirAccelerationLambda?: number;
  /** Deceleration lambda for mid-air movement when using the character controller */
  midAirDecelerationLambda?: number;
  /** Character controller options, set null to disable character controller */
  characterControllerOptions?: CharacterControllerOptions | null;
}

@EngineClass('CharacterMovementComponent')
export class CharacterMovementComponent extends BasePawnMovementComponent {
  @property()
  public maxSpeed: number = CHARACTER_MAX_SPEED;

  @property()
  public accelerationLambda: number = 100;

  @property()
  public decelerationLambda: number = 10;

  @property()
  public midAirAccelerationLambda: number = 5;

  @property()
  public midAirDecelerationLambda: number = 0.5;

  @property()
  public lookRightSpeed: number = 0.05;

  @property()
  public lookUpSpeed: number = 0.05;

  @property()
  public jumpSpeed: number = CHARACTER_JUMP_SPEED;

  @property()
  public maxMidAirJumps: number = 0;

  @property()
  public characterControllerOptions: CharacterControllerOptions | null = CharacterMovementComponent.DEFAULT_CHARACTER_CONTROLLER_OPTIONS;

  static get DEFAULT_CHARACTER_CONTROLLER_OPTIONS(): CharacterControllerOptions {
    return {
      offset: 0.01,
      simulatedGravityScale: 5,
      applyImpulsesToDynamicBodies: true,
      characterMass: CHARACTER_MASS,
      slideEnabled: true,
      maxSlopeClimbAngle: Math.PI / 4,
      minSlopeSlideAngle: Math.PI / 4,
      snapToGroundDistance: 0.1,
      autoStepConfig: {
        maxHeight: CHARACTER_HEIGHT / 4,
        minWidth: 0,
        includeDynamicBodies: false,
      }
    };
  }

  protected hasCharacterController: boolean = false;
  protected lastMotionTypeLogTime: number = 0;

  @property({ replicate: true, transient: true, hidden: true, net: { netType: 'bool', replicateTo: 'non-owner' } })
  protected isGrounded: boolean = false;

  @property({ replicate: true, transient: true, hidden: true, net: { netType: 'float', replicateTo: 'non-owner', quantization: { mode: 'scale', scale: 100 } } })
  protected forwardVelocity: number = 0;

  @property({ replicate: true, transient: true, hidden: true, net: { netType: 'float', replicateTo: 'non-owner', quantization: { mode: 'scale', scale: 100 } } })
  protected rightVelocity: number = 0;

  @property({ replicate: true, transient: true, hidden: true, net: { netType: 'float', replicateTo: 'non-owner', quantization: { mode: 'scale', scale: 100 } } })
  protected verticalVelocity: number = 0;

  protected jumpFrames: number = 0;
  protected lastGroundedTime: number = 0;
  protected coyoteTimeMs: number = 100; // 100ms grace period
  protected jumpsUsed: number = 0;
  protected camera: THREE.Camera | null | undefined = undefined;

  public override initialize(options?: CharacterMovementOptions): void {
    super.initialize(options);
    this.maxSpeed = options?.maxSpeed ?? this.maxSpeed;
    this.accelerationLambda = options?.accelerationLambda ?? this.accelerationLambda;
    this.decelerationLambda = options?.decelerationLambda ?? this.decelerationLambda;
    this.midAirAccelerationLambda = options?.midAirAccelerationLambda ?? this.midAirAccelerationLambda;
    this.midAirDecelerationLambda = options?.midAirDecelerationLambda ?? this.midAirDecelerationLambda;
    this.lookRightSpeed = options?.lookRightSpeed ?? this.lookRightSpeed;
    this.lookUpSpeed = options?.lookUpSpeed ?? this.lookUpSpeed;
    this.jumpSpeed = options?.jumpSpeed ?? this.jumpSpeed;
    this.maxMidAirJumps = options?.maxMidAirJumps ?? this.maxMidAirJumps;
    // Using !== undefined to allow explicit null (characterControllerOptions is nullable)
    if (options?.characterControllerOptions !== undefined) this.characterControllerOptions = options.characterControllerOptions;
  }

  public override beginPlay(): void {
    super.beginPlay();
    // Only create character controller on authority and autonomous proxy
    // Simulated proxies receive position from replication and don't need physics
    const owner = this.getActor();
    const shouldCreateController = owner && !owner.isSimulatedProxy();
    if (this.characterControllerOptions && shouldCreateController) {
      this.hasCharacterController = this.getPhysicsEngine()?.createCharacterController(this, this.characterControllerOptions) ?? false;
    }
  }

  public override endPlay(): void {
    super.endPlay();
    const owner = this.getActor();
    owner?.getPlayerController()?.getInputManager()?.exitPointerLock();
    this.getPhysicsEngine()?.removeCharacterController(this);
  }

  // ---------------------------------------------------------------------------
  // MovementMode ↔ isGrounded bridge
  // ---------------------------------------------------------------------------

  protected override getMovementMode(): MovementMode {
    return this.isGrounded ? MovementMode.Walking : MovementMode.Falling;
  }

  protected override setMovementMode(mode: MovementMode): void {
    this.setGrounded(mode === MovementMode.Walking);
  }

  // ---------------------------------------------------------------------------
  // Jump
  // ---------------------------------------------------------------------------

  public override jump(strength: number = 1): void {
    if (this.canJump()) {
      this.verticalVelocity = this.jumpSpeed * strength * this.jumpStrengthModifier;
      this.jumpFrames = 0;

      // Track mid-air jumps
      if (!this.isGrounded) {
        this.jumpsUsed++;
      }
    }
  }

  protected canJump(): boolean {
    if (this.getGrounded()) {
      return true;
    }
    return this.jumpsUsed < this.maxMidAirJumps;
  }

  // ---------------------------------------------------------------------------
  // Physics body positioning
  // ---------------------------------------------------------------------------

  /**
   * Override: when a character controller is present, position changes are applied
   * directly to rootComponent.position and the physics body is teleported immediately
   * so collision queries on the same tick start from the correct location.
   */
  public override setPawnWorldTransform(transform: { position?: THREE.Vector3, rotation?: THREE.Euler, scale?: THREE.Vector3 }): void {
    if (this.hasCharacterController) {
      const owner = this.getActor()!;
      const rootComponent = owner.rootComponent;
      if (transform.rotation) {
        rootComponent.rotation.copy(transform.rotation);
      }
      if (transform.scale) {
        rootComponent.scale.copy(transform.scale);
      }
      if (transform.position) {
        rootComponent.position.copy(transform.position);
        this.getPhysicsEngine()?.teleportBody(rootComponent as PrimitiveComponent, transform.position);
      }
    } else {
      super.setPawnWorldTransform(transform);
    }
  }

  // ---------------------------------------------------------------------------
  // Core movement step (full physics, used on both client and server)
  // ---------------------------------------------------------------------------

  /**
   * @param immediateBodySync - When true, the physics body position is also updated
   *   immediately via setTranslation so that subsequent KCC sweeps in the same tick
   *   start from the correct position. Use for all non-final steps in a multi-input batch
   *   (server drain) or correction replay. Must be false on the last/only step to preserve
   *   Rapier's computed velocity for applyImpulsesToDynamicBodies.
   */
  protected override performMovementStep(deltaTime: number, immediateBodySync: boolean = false): void {
    deltaTime = Math.min(deltaTime, 0.2); // HACK: prevent huge delta times (this should be done at the engine level at some point)

    const owner = this.getActor()!;
    const rootComponent = owner.rootComponent;

    const isMidAir = this.hasCharacterController && !this.isGrounded;
    const accelerationLambda = isMidAir ? this.midAirAccelerationLambda : this.accelerationLambda;
    const decelerationLambda = isMidAir ? this.midAirDecelerationLambda : this.decelerationLambda;
    const maxSpeed = this.maxSpeed * this.speedModifier;

    this.forwardVelocity = this.updateVelocity({
      input: this.forwardInput,
      maxSpeed,
      currentSpeed: this.forwardVelocity,
      accelerationLambda,
      decelerationLambda,
      deltaTime
    });

    this.rightVelocity = this.updateVelocity({
      input: this.rightInput,
      maxSpeed,
      currentSpeed: this.rightVelocity,
      accelerationLambda,
      decelerationLambda,
      deltaTime
    });

    const deltaPosition = new THREE.Vector3(0, 0, 0);
    deltaPosition.add(owner.forwardDirection().multiplyScalar(this.forwardVelocity * deltaTime));
    deltaPosition.add(owner.rightDirection().multiplyScalar(this.rightVelocity * deltaTime));

    if (this.hasCharacterController && rootComponent instanceof PrimitiveComponent) {
      const rootMotionType = rootComponent.getMotionType();
      if (rootMotionType !== PhysicsMotionType.KinematicPositionBased) {
        if (owner?.getPlayerController()) {
          // when the pawn is not possessed its collision might be disabled and we don't want to spam the error below in that case
          const now = Date.now();
          if (now - this.lastMotionTypeLogTime >= 10000) { // 10 seconds
            console.error(`${this.getPathName()}: Root component motion type should be "KinematicPositionBased" when using character controller, current type is "${rootMotionType}"`);
            this.lastMotionTypeLogTime = now;
          }
        }
      }

      // perform character controller movement with simulated gravity
      const physicsEngine = this.getPhysicsEngine()!;
      const gravityValue = physicsEngine.getOptions()!.gravity.y;
      this.verticalVelocity += gravityValue * deltaTime * this.characterControllerOptions!.simulatedGravityScale;
      deltaPosition.y += this.verticalVelocity * deltaTime;

      const { isGrounded, actualMovement } = physicsEngine.computeCharacterMovement(
        this, rootComponent, deltaPosition.toArray(), false, immediateBodySync
      );

      // Apply the collision-resolved movement directly to the Three.js transform.
      // The physics body was already updated inside computeCharacterMovement.
      rootComponent.position.x += actualMovement.x;
      rootComponent.position.y += actualMovement.y;
      rootComponent.position.z += actualMovement.z;

      // Confirm our position to Rapier on the next syncToPhysics; do not read it back.
      rootComponent.setPhysicsTransformUpdateFlags({
        sendPosition: true,
        sendRotation: false,
        receivePosition: false,
        receiveRotation: false,
      });

      this.isGrounded = isGrounded;
      if (this.isGrounded) {
        this.lastGroundedTime = performance.now();
        // Reset jump counter when landing
        this.jumpsUsed = 0;
      }

      this.jumpFrames += 1;
      // clear the vertical velocity when the character is grounded but only when it has jumped for a while
      // this is a hack around the issue that sometimes the character is pulled immediately back to the ground after jumping because the
      // isGrounded flag returned from the physics engine is not reliable...
      if (this.isGrounded && this.jumpFrames >= 10) {
        this.verticalVelocity = 0;
      }
    } else {
      rootComponent.addWorldPosition(deltaPosition);
    }

    if (this.lookRightInput.value !== 0) {
      const rotationAmount = this.calculateRotationAmount(this.lookRightInput, this.lookRightSpeed, deltaTime);
      rootComponent.addWorldRotation(MathHelpers.makeRotation({ yaw: rotationAmount }));
    }
  }

  // ---------------------------------------------------------------------------
  // State snapshot / restore
  // ---------------------------------------------------------------------------

  public override captureMovementState(): MovementStateSnapshot {
    return {
      ...super.captureMovementState(),
      forwardVelocity: this.forwardVelocity,
      rightVelocity: this.rightVelocity,
      verticalVelocity: this.verticalVelocity,
      isGrounded: this.isGrounded,
      jumpsUsed: this.jumpsUsed,
    };
  }

  /**
   * Restore movement state from a correction snapshot.
   *
   * Writes rootComponent.position directly and immediately teleports the physics
   * body so the first replay step's KCC sweep starts from the correct position.
   */
  public override restoreMovementState(snapshot: MovementStateSnapshot): void {
    super.restoreMovementState(snapshot);
    const owner = this.getActor()!;
    const rootComponent = owner.rootComponent;

    // Immediately sync the physics body so the first replay KCC sweep starts from
    // the correct position (body.translation() is used by computeColliderMovement).
    if (this.hasCharacterController && snapshot.position) {
      this.getPhysicsEngine()?.teleportBody(rootComponent as PrimitiveComponent, snapshot.position);
    }

    if (snapshot.forwardVelocity !== undefined) this.forwardVelocity = snapshot.forwardVelocity;
    if (snapshot.rightVelocity !== undefined) this.rightVelocity = snapshot.rightVelocity;
    if (snapshot.verticalVelocity !== undefined) this.verticalVelocity = snapshot.verticalVelocity;
    if (snapshot.isGrounded !== undefined) this.setGrounded(snapshot.isGrounded);
    if (snapshot.jumpsUsed !== undefined) this.jumpsUsed = snapshot.jumpsUsed;
  }

  // ---------------------------------------------------------------------------
  // Public accessors
  // ---------------------------------------------------------------------------

  public getGrounded(): boolean {
    return this.isGrounded || (performance.now() - this.lastGroundedTime < this.coyoteTimeMs);
  }

  public setGrounded(grounded: boolean): void {
    this.isGrounded = grounded;
    if (grounded) {
      this.lastGroundedTime = performance.now();
      this.jumpsUsed = 0;
    }
  }

  public getVelocities(): { forward: number, right: number, vertical: number } {
    return {
      forward: this.forwardVelocity,
      right: this.rightVelocity,
      vertical: this.verticalVelocity,
    };
  }

  public setVelocities(forward: number, right: number, vertical: number): void {
    this.forwardVelocity = forward;
    this.rightVelocity = right;
    this.verticalVelocity = vertical;
  }

  public onRepIsGrounded(): void {
    if (!this.getActor()?.isSimulatedProxy()) return;
    if (this.isGrounded) {
      // TODO: should this be replicated as well?
      this.lastGroundedTime = performance.now();
    }
  }

  public getMovementInputs(): { forward: number, right: number } {
    return {
      forward: this.forwardInput.value,
      right: this.rightInput.value,
    };
  }

  public getJumpState(): { jumpsUsed: number, jumpsRemaining: number, canJump: boolean } {
    return {
      jumpsUsed: this.jumpsUsed,
      jumpsRemaining: this.maxMidAirJumps - this.jumpsUsed,
      canJump: this.canJump(),
    };
  }

  //====================================================================================================

  protected updateVelocity(param: { input: PawnInput, maxSpeed: number, currentSpeed: number, accelerationLambda: number, decelerationLambda: number, deltaTime: number }): number {
    const inputValue = param.input?.value ?? 0;
    if (inputValue !== 0) {
      const target = THREE.MathUtils.clamp(inputValue * param.maxSpeed, -param.maxSpeed, param.maxSpeed);
      return THREE.MathUtils.damp(param.currentSpeed, target, param.accelerationLambda, param.deltaTime);
    } else {
      const newSpeed = THREE.MathUtils.damp(param.currentSpeed, 0, param.decelerationLambda, param.deltaTime);
      // Snap to zero when very close to prevent infinite gliding
      return Math.abs(newSpeed) < 0.1 ? 0 : newSpeed;
    }
  }

  protected calculateRotationAmount(input: PawnInput, speed: number, deltaTime: number): number {
    if (input.isAbsolute) {
      // For absolute input (like accumulated mouse movement), apply directly
      return input.value * speed;
    } else {
      // For speed-based input (like gamepad analog), apply with delta time
      return input.value * speed * deltaTime;
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Character';
  }
}
